// The nugget↔slide projection, as pure functions (TODO §17.2).
//
// Everything here is a plain transform over records — no storage, no dispatch, no clock. The
// two directions it defines are the whole of what "the same slide in five decks" means:
//
//   deck → nugget   `nuggetSlideFrom(slide)`      — what a local edit publishes.
//   nugget → deck   `materializeFrom(nugget)`     — what an open, an insert or another
//                                                   window's edit brings back.
//
// Both read `NUGGET_MIRRORED_FIELDS` and neither has its own list, which is the point:
// a field added to the type and not to that array is simply not shared, and a field added to
// the array is shared in both directions on the same day.

import {
  NUGGET_MIRRORED_FIELDS,
  type NuggetRecord,
  type NuggetSlide,
} from "@/types/nugget";
import type { SlideData } from "@/types/document";

// deck → nugget. The mirrored fields lifted off a deck's slide, plus the `id` the type needs
// (`NuggetSlide` keeps one only because `SlideData` requires one; it is never any deck's).
//
// Undefined values are carried through as undefined rather than dropped: clearing a subtitle
// is an edit, and a projection that only ever ADDS fields would leave the old one standing in
// four other decks.
export function nuggetSlideFrom(slide: SlideData): NuggetSlide {
  const out = { id: slide.id } as NuggetSlide;
  for (const field of NUGGET_MIRRORED_FIELDS) {
    // The assignment is field-by-field over a const tuple, which TypeScript cannot prove
    // aligns key with value across a union of nine field types. It does, by construction.
    (out as Record<string, unknown>)[field] = slide[field];
  }
  return out;
}

// nugget → deck. Just the mirrored fields, as a patch — deliberately NOT a whole `SlideData`,
// so a caller cannot accidentally overwrite `id`, `archived` or `level` (§17.2.1) by spreading
// the result. The three reducers that consume it all take a `Partial<SlideData>`.
export function materializeFrom(record: NuggetRecord): Partial<SlideData> {
  const out: Record<string, unknown> = {};
  for (const field of NUGGET_MIRRORED_FIELDS) {
    out[field] = record.slide[field];
  }
  return out as Partial<SlideData>;
}

// §17.4.6 The nugget's display name. Its slide's title, or a stated placeholder — the
// placeholder is UI, never storage: an untitled nugget is stored untitled, so titling the
// slide in any deck names it everywhere without a second field to keep in step.
export function nuggetDisplayName(name: string): string {
  return name.trim() || "Untitled nugget";
}

// §17.2.6 A nugget may be used at most once per deck (v1). Two slides in one OPEN deck linked
// to one nugget means two live Gridd embeds mounted on the same `griddDocId`, racing each
// other's ~400ms saves into a body four other decks are reading.
//
// Returns the offending slide, so the refusal can name it — "Already on slide 4" is an answer
// the user can act on; "can't do that" is not. Lifting the rule later means de-duplicating at
// mount, not just deleting this function.
//
// ARCHIVED slides are excluded, deliberately: an archived slide mounts no embed, so it cannot
// race anything, and refusing an insert because of a slide the user cannot see is the worse
// error. The gap that leaves is restoring an archived linked slide while a live one holds the
// same nugget — which the at-mount de-duplication above is the real fix for, and which this
// function cannot close on its own because it is not consulted on restore.
export function slideUsingNugget(
  slides: SlideData[],
  nuggetId: string,
): SlideData | undefined {
  return slides.find((s) => !s.archived && s.link?.nuggetId === nuggetId);
}
